import React from "react";
import { API_BASE_URL } from "@lib/config";
import { buildHarvestRecordBody } from "@lib/harvestRecord";
// Ladder groups: legacy one-contract lines harvested as one position (CIO 2026-09-17).
// The rule and every figure come from the server (strategy/harvest_cohorts.py); this only reads them.
import { describeCohort, readCohorts, type CohortRow } from "@lib/harvestCohorts";
// Which controls are on, said out loud: every control fails closed, so one that switches
// itself off looks exactly like a quiet day (CIO 2026-09-17). See lib/controlStatus.ts.
import { controlTone, describeControls, readControlStatus, type ControlStatus } from "@lib/controlStatus";
import {
  buildHarvestReleaseBody,
  interpretReleaseResponse,
  parseReleaseHistory,
  releaseReasonAllowance,
  describeReleaseClipping,
  categoryTag,
  MIN_RELEASE_REASONING_CHARS,
  RELEASE_REASON_CATEGORIES,
  RELEASE_RESEARCH_CONTEXT,
  RELEASE_RESEARCH_LABEL,
  RELEASE_HISTORY_GAPS,
  type ReleaseReasonCategory,
  type ReleaseHistoryEntry,
} from "@lib/harvestRelease";
import { HelionButton } from "@components/ui/HelionButton";
import { cardStyle, MICRO_LABEL, chipClass, fmtUSD } from "./index";

/**
 * Home-run capital recycling: the 2x candidates, the 60-day redeployment lockout, and
 * recording a manual TWS trim.
 *
 * WHY IT LIVES ON SIGNALS. Harvesting is a LEAPS decision and it needs regime context —
 * harvest window, early defense, VIX z — all of which is native to Signals and none of
 * which survives on Portfolio now that the regime strip has gone. Portfolio keeps one
 * line about locked capital in its audit strip, because cash under a 60-day lock is not
 * deployable and a book-of-record page that shows cash without saying so is exactly the
 * kind of reassurance this rework exists to remove. (CIO decision Q7, 2026-08-27.)
 *
 * WHERE IT SITS. Inside the "Home-run capital recycling" card in the Positioning row,
 * directly under the four summary tiles — one third of the row from 2xl, full width below
 * that. It used to be its own full-width band beneath the row (CIO, 2026-09-02: the page
 * is a once-a-day view and the band was mostly prose on a quiet day). Rows therefore
 * STACK — identity, then figures, then harvest + action — rather than running across;
 * the page's grid turns them two-up where the card is full width.
 *
 * THE CONTROL THIS CARRIES IS ONE OF ONLY TWO OF ITS KIND IN THE APP.
 * `POST /home_run_harvest {action:"record"}` is ONE of two reachable paths that start the
 * redeployment lockout; the other is marking a `home_run_trim` ticket EXECUTED on the
 * Trade Desk (wired 2026-09-02, `697f2ea8`). That path judges the FILL against the held
 * book, not the ticket, so a disposition that takes the whole line is a full exit and
 * deliberately starts NO lockout (P1-FULLEXIT, 2026-09-04). There is no backend timer
 * behind either — no cron, no sweeper. If this button is unreachable, harvested capital
 * recorded HERE silently loses its redeployment control and nothing anywhere reports that
 * it happened. Treat it accordingly: it does not move again without its replacement being
 * verified first.
 *
 * WHAT CHANGED FROM THE PORTFOLIO IMPLEMENTATION. That version collected quantity and
 * fill price through two chained `window.prompt()` dialogs, with the contract NOT on
 * screen while you typed — you were entering a fill price for a position you could no
 * longer see, into a box with no cancel beyond dismissing it, and a typo committed a
 * 60-day lock. This replaces them with a modal that shows the contract throughout,
 * validates both fields, states the consequence, and can be cancelled. Same endpoint,
 * same audit fields, stronger gate. This is a manual-execution record: it asserts what
 * the CIO already did in TWS. It transmits nothing to a broker.
 */

/* Candidate rows use `suggested*` keys (home_run_redeployment.py:377-380); only LOCK rows
 * use trimQuantity/runnerQuantity/harvestNotional. Reading the lock names on a candidate
 * yielded undefined, and `?? 0` then turned "unknown" into an asserted $0 -- so every 2x
 * line rendered "Estimated harvest $0" beside a live Record TWS Trim button. There is no
 * mapping layer: Signals passes the raw payload through with `as any`, which is why
 * TypeScript stayed silent. Both spellings are accepted so this cannot break on either. */
export type HarvestCandidate = {
  contractId?: string | number | null;
  suggestedTrimQty?: number | null;
  suggestedRunnerQty?: number | null;
  suggestedHarvestNotional?: number | null;
  estimatedHarvestNotional?: number | null;
  symbol?: string | null;
  accountId?: string | null;
  displayName?: string | null;
  quantity?: number | null;
  qty?: number | null;
  entryPrice?: number | null;
  marketPrice?: number | null;
  returnPct?: number | null;
  returnMultiple?: number | null;
  dte?: number | null;
  trimQuantity?: number | null;
  runnerQuantity?: number | null;
  harvestNotional?: number | null;
  canPartialTrim?: boolean | null;
  warning?: string | null;
};

/** Policy the backend publishes alongside the candidates. Read rather than assumed:
 *  the trigger multiple, the first-trim fraction and the lockout length are all
 *  configuration, and printing them as literals meant a config change would silently
 *  disagree with the screen. */
export type HarvestPolicy = {
  enabled?: boolean;
  trigger_multiple?: number | null;
  first_trim_pct?: number | null;
  redeploy_delay_days?: number | null;
  apply_to_buy_budget?: boolean;
};

/** _serialize_lock() (home_run_redeployment.py:391-414). Only the fields this panel reads
 *  are named; the row carries more. `notes` matters because the early-release control has
 *  to budget the reason string against the same nvarchar(1000) column the server appends
 *  to — see lib/harvestRelease.ts. */
export type HarvestLock = {
  lockId?: number | null;
  contractId?: string | number | null;
  displayName?: string | null;
  trimQuantity?: number | null;
  runnerQuantity?: number | null;
  harvestedNotional?: number | null;
  unlockAtUTC?: string | null;
  daysUntilUnlock?: number | null;
  status?: string | null;
  notes?: string | null;
};

export type HarvestPayload = {
  summary?: {
    activeLockCount?: number;
    lockedNotional?: number;
    nextUnlockAtUTC?: string | null;
    candidateCount?: number;
  } | null;
  activeLocks?: any[] | null;
  /** build_home_run_dashboard_payload -> _recent_lock_rows(): TOP 25 by CreatedAtUTC DESC,
   *  ALL statuses. The only source of past-release history this dashboard receives. */
  recentLocks?: any[] | null;
  candidates?: HarvestCandidate[] | null;
  policy?: (HarvestPolicy & Record<string, any>) | null;
} | null;

const num = (v: any): number | null => {
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

const candQty = (c: HarvestCandidate): number => num(c.quantity) ?? num(c.qty) ?? 0;
const candTrimQty = (c: HarvestCandidate, firstTrimPct?: number | null): number => {
  const explicit = num(c.suggestedTrimQty) ?? num(c.trimQuantity);
  if (explicit !== null && explicit > 0) return explicit;
  // Fallback only. The backend normally supplies suggestedTrimQty; when it does not, use
  // the POLICY fraction rather than assuming a half, so a config change to the documented
  // "50%-60% first trim" cannot leave this panel quietly saying 50%.
  const pct = num(firstTrimPct);
  const frac = pct !== null && pct > 0 && pct < 1 ? pct : 0.5;
  return Math.floor(candQty(c) * frac);
};
const candRunnerQty = (c: HarvestCandidate, firstTrimPct?: number | null): number | null => {
  const explicit = num(c.suggestedRunnerQty) ?? num(c.runnerQuantity);
  if (explicit !== null) return explicit;
  const q = candQty(c);
  const t = candTrimQty(c, firstTrimPct);
  return q > 0 ? q - t : null;
};
const candCanTrim = (c: HarvestCandidate, firstTrimPct?: number | null): boolean => {
  if (c.canPartialTrim === false) return false;
  const t = candTrimQty(c, firstTrimPct);
  const r = candRunnerQty(c, firstTrimPct);
  return t > 0 && r !== null && r > 0;
};
const candReturnLabel = (c: HarvestCandidate): string => {
  const mult = num(c.returnMultiple);
  if (mult !== null) return `${mult.toFixed(2)}x`;
  const pct = num(c.returnPct);
  return pct === null ? "—" : `${(pct * 100).toFixed(1)}%`;
};

const fmtWhen = (iso?: string | null): string => {
  if (!iso) return "—";
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toLocaleString() : "—";
};

/* --------------------------------- the modal -------------------------------- */

const RecordTrimModal: React.FC<{
  candidate: HarvestCandidate;
  busy: boolean;
  /** Lockout length in days, read from policy rather than assumed. */
  lockDays: number | null;
  firstTrimPct?: number | null;
  onCancel: () => void;
  onConfirm: (executedQty: number, fillPrice: number) => void;
}> = ({ candidate, busy, lockDays, firstTrimPct, onCancel, onConfirm }) => {
  const suggested = candTrimQty(candidate, firstTrimPct);
  const [qty, setQty] = React.useState(suggested > 0 ? String(suggested) : "");
  const [price, setPrice] = React.useState(
    candidate.marketPrice != null ? String(candidate.marketPrice) : ""
  );

  const qtyNum = num(qty);
  const priceNum = num(price);
  const qtyValid = qtyNum !== null && qtyNum > 0 && qtyNum <= candQty(candidate);
  const priceValid = priceNum !== null && priceNum > 0;
  const runnerLeft = qtyValid ? candQty(candidate) - (qtyNum as number) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Record TWS trim"
    >
      <div className="w-full max-w-lg rounded-xl p-5" style={cardStyle}>
        <div className={MICRO_LABEL} style={{ color: "var(--text-muted)" }}>
          Record a trim you already executed in TWS
        </div>

        {/* The contract stays on screen the whole time. The dialogs this replaces did
            not show it, so you typed a fill price for a position you could not see. */}
        <div className="mt-2 text-base font-semibold" style={{ color: "var(--text-primary)" }}>
          {candidate.displayName ?? String(candidate.contractId ?? "—")}
        </div>
        <div className="mt-1 flex flex-wrap gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
          <span>Held <strong style={{ color: "var(--text-primary)" }}>{candQty(candidate) || "—"}</strong></span>
          <span>Return <strong style={{ color: "var(--text-primary)" }}>{candReturnLabel(candidate)}</strong></span>
          <span>DTE <strong style={{ color: "var(--text-primary)" }}>{candidate.dte ?? "—"}</strong></span>
          <span>Suggested trim <strong style={{ color: "var(--text-primary)" }}>{suggested || "—"}</strong></span>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>Contracts sold</span>
            <input
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="rounded border px-2 py-1.5 text-sm"
              style={{
                backgroundColor: "var(--bg-secondary)",
                borderColor: "var(--card-border)",
                color: "var(--text-primary)",
              }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>Average fill price</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="rounded border px-2 py-1.5 text-sm"
              style={{
                backgroundColor: "var(--bg-secondary)",
                borderColor: "var(--card-border)",
                color: "var(--text-primary)",
              }}
            />
          </label>
        </div>

        {/* Say what will happen before it happens. */}
        <div
          className="mt-4 rounded border px-3 py-2 text-xs leading-relaxed"
          style={{ borderColor: "var(--card-border)", color: "var(--text-muted)" }}
        >
          {qtyValid && priceValid ? (
            <>
              Records <strong style={{ color: "var(--text-primary)" }}>{qtyNum}</strong> contract
              {qtyNum === 1 ? "" : "s"} sold at{" "}
              <strong style={{ color: "var(--text-primary)" }}>{fmtUSD(priceNum)}</strong>
              {runnerLeft !== null ? (
                <> , leaving a runner of <strong style={{ color: "var(--text-primary)" }}>{runnerLeft}</strong></>
              ) : null}
              . This starts a{" "}
              <strong style={{ color: "var(--text-primary)" }}>
                {lockDays === null
                  ? "redeployment lockout for the configured period"
                  : `${lockDays}-calendar-day redeployment lockout`}
              </strong>{" "}
              on the harvested proceeds. It records an execution you
              have already made in TWS — nothing is sent to a broker.
            </>
          ) : (
            <>
              Enter the contracts sold and the average fill price from TWS. Quantity must be at
              least 1 and no more than the {candQty(candidate) || "—"} held.
            </>
          )}
        </div>

        {candidate.warning ? (
          <div className={`${chipClass("warning")} mt-3`}>{candidate.warning}</div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <HelionButton size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </HelionButton>
          <HelionButton
            size="sm"
            disabled={busy || !qtyValid || !priceValid}
            onClick={() => onConfirm(qtyNum as number, priceNum as number)}
          >
            {busy ? "Recording…" : "Record trim & start lockout"}
          </HelionButton>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------- the early-release gate ------------------------- */

/**
 * THE CIO'S BYPASS, WITH REASONING.
 *
 * "Keep the lockout. When we get the 2x we trade the half like we've talked about, locking
 * out the capital and starting the timer — but give me the ability to unlock the capital
 * with a click of a button and utilise it if I see an opportunity."
 *
 * The rule stays. This is the recorded override. The reasoning capture is the whole point:
 * the lockout is ADVISORY — nothing in this app blocks a buy because capital is locked —
 * so without a recorded override he can already ignore the number on screen and no track
 * record accumulates. Making the override deliberate and attributable is the only thing
 * this control adds that ignoring the number does not.
 *
 * It deliberately copies the Record-TWS-Trim gate above: the contract stays on screen while
 * he types, the consequence is spelled out as a full sentence before he commits, and cancel
 * is always available. Same shape, so the second irreversible action in this card behaves
 * like the first.
 *
 * FULL RELEASE ONLY. `release_home_run_lock()` sets Status='RELEASED' on the whole row;
 * there is no partial column, so a part-release would need a migration. The amount that
 * will be freed is therefore shown, not chosen.
 */
const EarlyReleaseModal: React.FC<{
  lock: HarvestLock;
  busy: boolean;
  recordedBy: string;
  onCancel: () => void;
  onConfirm: (category: ReleaseReasonCategory, reasoning: string) => void;
}> = ({ lock, busy, recordedBy, onCancel, onConfirm }) => {
  const [category, setCategory] = React.useState<ReleaseReasonCategory | "">("");
  const [reasoning, setReasoning] = React.useState("");

  // P2-REASONLENGTH, 2026-09-04. This used to budget the reason against whatever space the
  // lock's EXISTING notes left behind, which on a lock recorded from the Trade Desk with a
  // thorough "why" fell to zero at about 919 characters — the modal then disabled its own
  // button and told the CIO to ask the desk. The Trade Desk is the path most harvests take,
  // so the control was being withdrawn exactly when he had explained himself properly.
  // The budget is now the release line's OWN capacity, which never depends on the old note;
  // the server clips the older note to make room and says how much it clipped.
  const allowance = releaseReasonAllowance({ existingNotes: lock.notes ?? null, recordedBy });
  const tagCost = category ? categoryTag(category as ReleaseReasonCategory).length : 0;
  const roomForText = Math.max(0, allowance.maxChars - tagCost);

  const trimmed = reasoning.trim();
  const categoryValid = category !== "";
  const reasoningValid = trimmed.length >= MIN_RELEASE_REASONING_CHARS && roomForText >= MIN_RELEASE_REASONING_CHARS;
  const valid = categoryValid && reasoningValid;

  // What the older note will lose, given the reason as it stands right now. Shown before he
  // commits so a clip is something he was told about, not something he discovers.
  const clipping = describeReleaseClipping({
    existingNotes: lock.notes ?? null,
    recordedBy,
    reason: category ? `${categoryTag(category as ReleaseReasonCategory)}${trimmed}` : trimmed,
  });

  const amount = num(lock.harvestedNotional);
  const daysLeft = num(lock.daysUntilUnlock);
  const selected = RELEASE_REASON_CATEGORIES.find((c) => c.code === category) ?? null;

  const inputStyle: React.CSSProperties = {
    backgroundColor: "var(--bg-secondary)",
    borderColor: "var(--card-border)",
    color: "var(--text-primary)",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Release locked capital early"
    >
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl p-5" style={cardStyle}>
        <div className={MICRO_LABEL} style={{ color: "var(--text-muted)" }}>
          Release locked capital before the clock runs out
        </div>

        {/* The contract and the money stay on screen the whole time he is typing. */}
        <div className="mt-2 text-base font-semibold" style={{ color: "var(--text-primary)" }}>
          {lock.displayName ?? String(lock.contractId ?? "—")}
        </div>
        <div className="mt-1 flex flex-wrap gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
          <span>
            Under lock <strong style={{ color: "var(--text-primary)" }}>{fmtUSD(amount)}</strong>
          </span>
          <span>
            Unlocks <strong style={{ color: "var(--text-primary)" }}>{fmtWhen(lock.unlockAtUTC ?? null)}</strong>
          </span>
          <span>
            Days left{" "}
            <strong style={{ color: "var(--text-primary)" }}>{daysLeft === null ? "—" : daysLeft}</strong>
          </span>
        </div>

        {/* Research context, next to the decision and short enough to actually be read. */}
        <div
          className="mt-3 rounded border px-3 py-2 text-[11px] leading-relaxed"
          style={{ borderColor: "var(--card-border)", color: "var(--text-muted)" }}
        >
          <div className={MICRO_LABEL} style={{ color: "var(--text-muted)" }}>
            Research context
          </div>
          <ul className="mt-1 list-disc pl-4 space-y-0.5">
            {RELEASE_RESEARCH_CONTEXT.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <div className="mt-1.5 italic">{RELEASE_RESEARCH_LABEL}</div>
        </div>

        <div className="mt-4 grid gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              Reason category (required)
            </span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ReleaseReasonCategory | "")}
              className="rounded border px-2 py-1.5 text-sm"
              style={inputStyle}
            >
              <option value="">Choose a category…</option>
              {RELEASE_REASON_CATEGORIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
            {selected ? (
              <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                {selected.help}
              </span>
            ) : null}
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              Your reasoning (required) — this is what makes the override worth having
            </span>
            <textarea
              rows={4}
              value={reasoning}
              maxLength={Math.max(roomForText, 1)}
              onChange={(e) => setReasoning(e.target.value)}
              placeholder="What are you buying with this capital, and why now rather than at the unlock date?"
              className="rounded border px-2 py-1.5 text-sm"
              style={inputStyle}
            />
            <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
              {trimmed.length}/{roomForText} characters · at least {MIN_RELEASE_REASONING_CHARS}. Recorded on the
              audit trail against your name.
            </span>
          </label>
        </div>

        {/* Say what will happen, in a full sentence, before it happens. */}
        <div
          className="mt-4 rounded border px-3 py-2 text-xs leading-relaxed"
          style={{ borderColor: "var(--card-border)", color: "var(--text-muted)" }}
        >
          {valid ? (
            <>
              Releases <strong style={{ color: "var(--text-primary)" }}>{fmtUSD(amount)}</strong> in full
              {daysLeft !== null ? (
                <>
                  {" "}
                  <strong style={{ color: "var(--text-primary)" }}>
                    {daysLeft} day{daysLeft === 1 ? "" : "s"}
                  </strong>{" "}
                  before the lockout would have expired
                </>
              ) : null}
              . This lockout ends permanently — it cannot be restarted from this screen. What ends is the
              withholding of this capital on screen; the buy budget itself only changes if lockout policy is
              applying this lock to it (apply_to_buy_budget) — when it is not, the budget does not change.
              The release is written to the audit trail with your category and reasoning against your name. It sends nothing to a broker and executes no trade.
            </>
          ) : (
            <>
              Choose a category and write at least {MIN_RELEASE_REASONING_CHARS} characters of reasoning. The
              release is full and permanent: there is no partial release, and this lockout cannot be restarted
              from this screen.
            </>
          )}
        </div>

        {/* A full notes field no longer blocks the release; it only costs the older note
            some characters, and that is said out loud rather than discovered afterwards. */}
        {clipping.willClip ? (
          <div className={`${chipClass("warning")} mt-3`}>{clipping.note}</div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <HelionButton size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </HelionButton>
          <HelionButton
            size="sm"
            disabled={busy || !valid}
            onClick={() => onConfirm(category as ReleaseReasonCategory, trimmed)}
          >
            {busy ? "Releasing…" : `Release ${fmtUSD(amount)} now`}
          </HelionButton>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------ the brake: history --------------------------- */

/**
 * Recent early releases, directly above the control. NOT filtered to the signed-in user:
 * parseReleaseHistory (lib/harvestRelease.ts:501-536) filters only on status === "RELEASED"
 * and never compares releasedBy.
 *
 * He asked for the button; this is what stops it becoming automatic. A release that has to
 * sit under a list of the last four is a different decision from one taken in isolation.
 *
 * WHAT IS MISSING IS STATED, NOT INVENTED. `_serialize_lock()` publishes no release
 * timestamp (UpdatedAtUTC is neither SELECTed by `_recent_lock_rows()` nor serialized), so
 * the DATE of a past release, and therefore how many days early it was, cannot be derived
 * from any payload this dashboard receives. Deriving one from `unlockAtUTC` would be wrong
 * by up to the whole lock period. The section says so rather than going quiet.
 */
const ReleaseHistory: React.FC<{ entries: ReleaseHistoryEntry[]; available: boolean }> = ({
  entries,
  available,
}) => (
  <div className="mt-3">
    <div className={MICRO_LABEL} style={{ color: "var(--text-muted)" }}>
      Recent early releases
    </div>
    {!available ? (
      /* Reachable: get_portfolio_view's exception fallback publishes a homeRun block with no
         `recentLocks` key at all. Say the history is missing rather than rendering an empty
         list, which would read as "he has never done this". */
      <div className={`${chipClass("warning")} mt-1.5`}>
        Release history unavailable — this payload carries no <code>recentLocks</code>, so past overrides
        cannot be listed. That is NOT the same as there having been none.
      </div>
    ) : entries.length === 0 ? (
      <div className="mt-1.5 text-xs" style={{ color: "var(--text-muted)" }}>
        No lockout has been released early. The first one appears here, with its amount and your reasoning.
      </div>
    ) : (
      <div className="mt-1.5 grid gap-1.5">
        {entries.slice(0, 5).map((e, i) => (
          <div
            key={e.lockId ?? i}
            className="rounded px-2.5 py-2"
            style={{ backgroundColor: "var(--table-row-alt-bg)" }}
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0 truncate text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                {e.displayName}
              </div>
              <div className="shrink-0 text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                {fmtUSD(e.amount)}
              </div>
            </div>
            <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-muted)" }}>
              {e.category ?? "UNCATEGORISED"} · would have unlocked {fmtWhen(e.unlockAtUTC)}
              {e.releasedBy ? ` · ${e.releasedBy}` : ""}
            </div>
            <div className="mt-0.5 text-xs" style={{ color: "var(--text-secondary)" }}>
              {e.reasoning || "No reasoning recorded."}
            </div>
          </div>
        ))}
      </div>
    )}
    {available ? (
      <div className="mt-1.5 text-[11px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
        {RELEASE_HISTORY_GAPS.note}
      </div>
    ) : null}
  </div>
);

/* --------------------------------- the panel -------------------------------- */

/**
 * Ladder groups: the legacy one-contract lines that cannot be trimmed alone, shown as the
 * positions they are treated as. Every figure is the server's (strategy/harvest_cohorts.py);
 * nothing is recomputed here, so this card and the engine that stages the ticket cannot
 * disagree. There is no action: when a group reaches its combined trigger the exit engine
 * stages the sell ticket, and it is approved on the Trade Desk like any other.
 */
/**
 * The controls line. Collapsed to one sentence when everything is on, because a green light
 * every day is what makes the one red day visible; expanded per control the moment any of them
 * is not on, because that is the state that used to be silent.
 */
const ControlsLine: React.FC<{ status: ControlStatus | null }> = ({ status }) => {
  const verdict = describeControls(status);
  const notes = (status?.controls || []).filter((c) => c.state !== "on");
  return (
    <div
      className="mt-3 rounded-md px-2.5 py-2"
      style={{
        backgroundColor: verdict.tone === "positive" ? "var(--bg-secondary)" : "rgb(var(--c-warn) / 0.08)",
        border: `1px solid ${verdict.tone === "positive" ? "var(--card-border)" : "rgb(var(--c-warn) / 0.45)"}`,
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-1.5">
        <div className={MICRO_LABEL} style={{ color: "var(--text-muted)" }}>
          Controls
        </div>
        <span className={chipClass(verdict.tone === "danger" ? "danger" : verdict.tone === "warning" ? "warning" : verdict.tone === "positive" ? "positive" : "neutral")}>
          {status ? status.summary || verdict.text : "unavailable"}
        </span>
      </div>
      <div className="mt-1 text-[11px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
        {verdict.text}
      </div>
      {notes.length ? (
        <div className="mt-1.5 space-y-1">
          {notes.map((c) => (
            <div key={c.key} className="flex flex-wrap items-baseline gap-1.5 text-[11px]" style={{ color: "var(--text-muted)" }}>
              <span className={chipClass(controlTone(c.state))}>{c.label}</span>
              <span>{c.detail}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const LadderGroups: React.FC<{ rows: CohortRow[] }> = ({ rows }) => (
  <div className="mt-3">
    <div className={MICRO_LABEL} style={{ color: "var(--text-muted)" }}>
      Ladder groups — one-contract lines harvested together
    </div>
    <div className="mt-1 text-[11px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
      A single contract cannot be half-trimmed, so neighbouring one-lots in an expiry are treated as one
      position: the group triggers on its combined multiple, the deepest in the money is sold, and the rest
      stay as runners. Once per group.
    </div>
    <div className="mt-1.5 grid gap-1.5 md:grid-cols-2 2xl:grid-cols-1 min-[1800px]:grid-cols-2">
      {rows.map((row) => {
        const verdict = describeCohort(row);
        return (
          <div
            key={row.key}
            className="rounded-md px-2.5 py-2"
            style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--card-border)" }}
          >
            <div className="flex flex-wrap items-center justify-between gap-1.5">
              <div className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>
                {row.key} · {row.label}
              </div>
              <span className={chipClass(verdict.tone)}>{verdict.headline}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]" style={{ color: "var(--text-muted)" }}>
              {row.members.map((m) => (
                <span key={m.conid} title={m.excludedReason || undefined} style={m.inGroup ? undefined : { opacity: 0.55 }}>
                  {m.strike !== null ? m.strike.toLocaleString() : "?"}{" "}
                  <strong style={{ color: "var(--text-primary)" }}>
                    {m.multiple !== null ? `${m.multiple.toFixed(2)}x` : "—"}
                  </strong>
                  {m.inGroup ? "" : m.quantity && m.quantity > 1 ? " (own runner)" : " (not held)"}
                </span>
              ))}
            </div>
            <div className="mt-1 text-[11px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
              {verdict.detail}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export const HarvestDecisionPanel: React.FC<{
  homeRun: HarvestPayload;
  userEmail?: string | null;
  /** Called after a successful record so the host page can reload its surfaces. */
  onRecorded?: () => void | Promise<void>;
  /** Called after an early release, for the same reason. Falls back to onRecorded. */
  onReleased?: () => void | Promise<void>;
  className?: string;
}> = ({ homeRun, userEmail, onRecorded, onReleased, className = "" }) => {
  const [target, setTarget] = React.useState<HarvestCandidate | null>(null);
  const [releaseTarget, setReleaseTarget] = React.useState<HarvestLock | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState<{ tone: "success" | "error"; text: string } | null>(null);

  const candidates = Array.isArray(homeRun?.candidates) ? (homeRun!.candidates as HarvestCandidate[]) : [];
  const cohorts = readCohorts(homeRun);
  const controlStatus = readControlStatus(homeRun);
  const activeLocks = Array.isArray(homeRun?.activeLocks) ? (homeRun!.activeLocks as any[]) : [];
  // The handler's own default when recordedBy is absent is "CIO" (home_run_harvest
  // /__init__.py:66) — mirrored here so the length budget the modal shows is computed
  // against exactly the string that will be sent.
  const recordedBy = (userEmail || "").trim() || "CIO";
  const releaseHistory = React.useMemo(
    () => parseReleaseHistory(homeRun?.recentLocks ?? null),
    [homeRun]
  );
  // The builder falls back to {policy:{}, summary:{lockedNotional:0}} on any exception,
  // so an unguarded read would render a confident "no lockout" when the truth is "we do
  // not know". A boolean `enabled` is the marker that the policy actually loaded.
  const policyLoaded = typeof homeRun?.policy?.enabled === "boolean";
  // Read, never assumed. These are configuration (exit_signals.json -> load_home_run_policy)
  // and printing them as literals meant a config change would silently disagree with what
  // this panel says. Null when the policy block did not load, which the copy then admits.
  const triggerMultiple = policyLoaded ? num(homeRun?.policy?.trigger_multiple) : null;
  const firstTrimPct = policyLoaded ? num(homeRun?.policy?.first_trim_pct) : null;
  const lockDays = policyLoaded ? num(homeRun?.policy?.redeploy_delay_days) : null;
  // Reads as English in both states: "A line at 2x ..." / "A line at the threshold ...".
  const triggerLabel = triggerMultiple !== null ? `${triggerMultiple}x` : "the threshold";

  const record = React.useCallback(
    async (candidate: HarvestCandidate, executedQty: number, fillPrice: number) => {
      const contractId = String(candidate.contractId ?? "").trim();
      if (!contractId) return;
      setBusy(true);
      setNote(null);
      try {
        const res = await fetch(`${API_BASE_URL}/home_run_harvest`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // Built by a pure, unit-pinned function (lib/harvestRecord.ts). Notably it does
          // NOT send returnPct: the backend uses a supplied one verbatim, and the
          // candidate's is struck on the book MARK rather than the price actually
          // transacted. Omitting it makes the backend derive the realized return from the
          // fill price entered above, which is also what lets its below-trigger warning
          // fire on a trim that was under 2x at the fill.
          body: JSON.stringify(
            buildHarvestRecordBody({
              contractId,
              symbol: candidate.symbol,
              accountId: candidate.accountId,
              displayName: candidate.displayName,
              originalQuantity: candQty(candidate) || null,
              executedQty,
              fillPrice,
              entryPrice: candidate.entryPrice,
              recordedBy: userEmail,
            })
          ),
        });
        const payload = await res.json().catch(() => null);
        if (!res.ok) throw new Error(payload?.error || `home_run_harvest failed: ${res.status}`);
        setTarget(null);
        // The backend returns warnings in its SUCCESS body -- notably "Recorded return is
        // below the configured home-run trigger" (home_run_redeployment.py:561-568).
        // Dropping them voided the point of omitting returnPct from the request: we made
        // the server derive the real return from the fill so that warning COULD fire, then
        // discarded it. It is exactly the case this panel warns about, since a line at
        // 2.0x on the mark can be under 2x at the bid.
        const warns = Array.isArray(payload?.warnings)
          ? (payload.warnings as unknown[]).filter((w) => typeof w === "string" && w.trim())
          : [];
        const tail = `Next unlock ${fmtWhen(
          payload?.summary?.nextUnlockUTC ?? payload?.lock?.unlockAtUTC ?? null
        )}. The same execution will also appear in the Trade Desk break queue as a fill with no reason recorded — claim it there so the ledger has it too.`;
        setNote({
          tone: warns.length ? "error" : "success",
          text: warns.length
            ? `Harvest recorded WITH WARNINGS: ${warns.join(" | ")} The lockout was still started. ${tail}`
            : `Harvest recorded. ${tail}`,
        });
        if (onRecorded) await onRecorded();
      } catch (err: any) {
        setNote({
          tone: "error",
          text: `${err?.message || "Failed to record the harvest."} No lockout was started.`,
        });
      } finally {
        setBusy(false);
      }
    },
    [onRecorded, userEmail]
  );

  /**
   * The early release. Same endpoint as the record above, `action:"release"` branch.
   *
   * TWO THINGS THIS DELIBERATELY DOES NOT DO.
   *  1. It never claims a release from HTTP 200. `release_home_run_lock` updates
   *     `WHERE LockId = ? AND Status = 'ACTIVE'`, so a release can legitimately touch zero
   *     rows (already released, already expired, wrong id). The server turns that into a
   *     400, but the body is checked regardless — interpretReleaseResponse decides, not the
   *     status code, and an unrecognised 200 is reported as UNCONFIRMED rather than as done.
   *  2. It never disables, greys out or refuses anything on the buy side. The lockout is
   *     advisory before this control and advisory after it; all this changes is that the
   *     override is recorded.
   */
  const release = React.useCallback(
    async (lock: HarvestLock, category: ReleaseReasonCategory, reasoning: string) => {
      setBusy(true);
      setNote(null);
      let body;
      try {
        body = buildHarvestReleaseBody({
          lockId: lock.lockId,
          category,
          reasoning,
          recordedBy: userEmail,
          existingNotes: lock.notes ?? null,
        });
      } catch (err: any) {
        setBusy(false);
        setNote({
          tone: "error",
          text: `${err?.message || "The release request could not be built."} Nothing was released.`,
        });
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/home_run_harvest`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const payload = await res.json().catch(() => null);
        const outcome = interpretReleaseResponse(res.status, payload, body.lockId);
        // The server reports what it had to clip out of the lock's notes to fit the release
        // record (home_run_redeployment._fit_release_note). Say it rather than let a silent
        // truncation be discovered later by whoever reads the row.
        const clipDetail =
          outcome.released && (payload as any)?.notesClipped === true
            ? String((payload as any)?.notesClipping?.detail || "").trim()
            : "";
        setNote({
          tone: outcome.released ? "success" : "error",
          text: clipDetail ? `${outcome.message} ${clipDetail}` : outcome.message,
        });
        if (outcome.released) setReleaseTarget(null);
        // Reload either way: on an UNCONFIRMED answer the panel must show the lock's real
        // current state rather than whatever it believed a moment ago.
        const reload = onReleased ?? onRecorded;
        if (reload) await reload();
      } catch (err: any) {
        setNote({
          tone: "error",
          text: `${err?.message || "The release request failed."} Treat it as UNCONFIRMED — reload the panel and check whether the lockout is still active before deploying anything.`,
        });
      } finally {
        setBusy(false);
      }
    },
    [onRecorded, onReleased, userEmail]
  );

  return (
    <div className={className}>
      {/* NO summary tiles and NO card header here. The host page owns those -- Signals
          renders locked notional, active clocks, 2x candidates and next unlock from the
          SAME payload directly above this, in the same card. Repeating them inside the
          panel put the identical four figures on screen twice, which is precisely the
          double-statement this rework exists to delete. This panel contributes only what
          is not already there: the lockouts, the candidates, and the record action. */}
      {!policyLoaded ? (
        <div className={`${chipClass("warning")} mb-2`}>
          Lockout policy not loaded - the figures above cannot be trusted
        </div>
      ) : null}

      {note ? (
        <div
          className="mb-3 rounded border px-3 py-2 text-xs leading-relaxed"
          style={{
            borderColor: "var(--card-border)",
            color: note.tone === "error" ? "var(--status-danger-text)" : "var(--text-primary)",
          }}
        >
          {note.text}
        </div>
      ) : null}

      {activeLocks.length > 0 ? (
        <>
          {/* THE BRAKE, directly above the control. What he has already overridden, and
              why, before he overrides another one. */}
          <ReleaseHistory entries={releaseHistory} available={Array.isArray(homeRun?.recentLocks)} />

          <div className="mt-3">
            <div className={MICRO_LABEL} style={{ color: "var(--text-muted)" }}>
              Active redeployment lockouts
            </div>
            {/* Two-up while the card is full width (below 2xl); one-up from 2xl, where the
                card is half the row (~740px at 1536px); two-up again from 1800px, where each
                half-row is wide enough. Same breakpoints as the candidate and group lists. */}
            <div className="mt-1.5 grid gap-1.5 md:grid-cols-2 2xl:grid-cols-1 min-[1800px]:grid-cols-2">
              {activeLocks.slice(0, 6).map((lock: any, i: number) => (
                <div
                  key={lock?.lockId ?? lock?.contractId ?? i}
                  className="rounded px-2.5 py-2"
                  style={{ backgroundColor: "var(--table-row-alt-bg)" }}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                        {lock?.displayName ?? lock?.contractId ?? "—"}
                      </div>
                      <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                        Trim {lock?.trimQuantity ?? "—"} / runner {lock?.runnerQuantity ?? "—"} · unlock{" "}
                        {fmtWhen(lock?.unlockAtUTC ?? null)}
                      </div>
                    </div>
                    <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                      {fmtUSD(lock?.harvestedNotional ?? 0)}
                    </div>
                  </div>
                  {/* ONE CLICK OPENS IT; committing takes a category and reasoning. A lock
                      row with no lockId cannot be addressed by the endpoint
                      (home_run_harvest/__init__.py:60-62), so say that rather than offering
                      a button that would 400. */}
                  <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                      {num(lock?.daysUntilUnlock) === null
                        ? "Advisory — nothing blocks a buy"
                        : `${num(lock?.daysUntilUnlock)} day${num(lock?.daysUntilUnlock) === 1 ? "" : "s"} left · advisory, nothing blocks a buy`}
                    </div>
                    {num(lock?.lockId) === null ? (
                      <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                        No lock id — release unavailable
                      </span>
                    ) : (
                      <HelionButton size="sm" disabled={busy} onClick={() => setReleaseTarget(lock as HarvestLock)}>
                        Release early…
                      </HelionButton>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}

      <div className="mt-3">
        <div className={MICRO_LABEL} style={{ color: "var(--text-muted)" }}>
          Candidates requiring CIO review
        </div>
        {/* The return multiple below is struck on the BOOK MARK, not on a price you can
            transact at. Candidate detection computes (market_price - entry_price) /
            entry_price against the 2.0x trigger (home_run_redeployment.py), and
            market_price is whatever the quote feed last wrote -- it has been the bid, and
            as of 2026-08-27 it is the mid. A line can therefore show 2.0x on the mark
            while being under 2x at the bid you would actually sell into, and recording a
            trim starts a 60-day lockout. Say so rather than letting the multiple read as
            an executable figure. The modal below is unaffected: it records your ACTUAL
            fill price, and the backend re-derives the return from it.
            This is also the ONLY place the manual gate is stated now that the card's
            footnote is gone: execute in TWS first, record here, nothing goes to a broker. */}
        {/* Shown only when there is a candidate to act on (CIO, 2026-09-18): on a quiet day it
            was two lines of instructions for a button that does not exist. It still sits
            directly above every Record TWS Trim action, which is where the manual gate and the
            book-mark caveat have to be read. */}
        {candidates.length > 0 ? (
          <div className="mt-1 text-[11px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
            Multiples are struck on the book mark — a line at {triggerLabel} on the mark can be under{" "}
            {triggerLabel} at the bid, so check the quote first. Execute in TWS, then record the trim
            here: that starts the{" "}
            {lockDays !== null ? `${lockDays}-day` : "irreversible"} redeployment lockout and sends
            nothing to a broker.
          </div>
        ) : null}
        {candidates.length > 0 ? (
          <div className="mt-1.5 grid gap-1.5 md:grid-cols-2 2xl:grid-cols-1 min-[1800px]:grid-cols-2">
            {candidates.slice(0, 8).map((c, i) => {
              const blocked = !candCanTrim(c, firstTrimPct);
              return (
                <div
                  key={String(c.contractId ?? i)}
                  className="rounded px-3 py-2"
                  style={{ backgroundColor: "var(--table-row-alt-bg)" }}
                >
                  {/* Stacked, not side by side: this row has to fit a third-width column.
                      The return multiple is the trigger, so it takes the headline slot on
                      the right; the rest of the figures run on one muted line beneath. */}
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="min-w-0 truncate text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                      {c.displayName ?? String(c.contractId ?? "—")}
                    </div>
                    <div className="shrink-0 text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                      {candReturnLabel(c)}
                    </div>
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs" style={{ color: "var(--text-muted)" }}>
                    <span>Qty <strong style={{ color: "var(--text-primary)" }}>{candQty(c) || "—"}</strong></span>
                    <span>DTE <strong style={{ color: "var(--text-primary)" }}>{c.dte ?? "—"}</strong></span>
                    <span>Trim <strong style={{ color: "var(--text-primary)" }}>{candTrimQty(c, firstTrimPct) || 0}</strong></span>
                    <span>Runner <strong style={{ color: "var(--text-primary)" }}>{candRunnerQty(c, firstTrimPct) ?? "—"}</strong></span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                      Est. harvest{" "}
                      <strong style={{ color: "var(--text-primary)" }}>
                        {/* No `?? 0`: fmtUSD withholds a null as an em-dash, and an unknown
                            harvest must read as unknown, not as an asserted zero. */}
                        {fmtUSD(
                          num(c.suggestedHarvestNotional) ??
                            num(c.estimatedHarvestNotional) ??
                            num(c.harvestNotional)
                        )}
                      </strong>
                    </div>
                    <HelionButton size="sm" disabled={blocked || busy} onClick={() => setTarget(c)}>
                      Record TWS Trim
                    </HelionButton>
                  </div>
                  {blocked ? (
                    <div className={`${chipClass("warning")} mt-2`}>
                      {c.warning ||
                        `Single-contract line — no runner would remain, so no trim can be recorded here. A CIO exception until new entries meet the 2-contract harvestability standard.`}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-1.5 text-xs" style={{ color: "var(--text-muted)" }}>
            No line is above the home-run threshold{triggerMultiple !== null ? ` (${triggerMultiple}x)` : ""}.
            A qualifying line appears here with its trim, runner, estimated harvest and the Record
            TWS Trim action.
          </div>
        )}
        {cohorts.length > 0 ? <LadderGroups rows={cohorts} /> : null}
        <ControlsLine status={controlStatus} />
      </div>

      {target ? (
        <RecordTrimModal
          candidate={target}
          busy={busy}
          lockDays={lockDays}
          firstTrimPct={firstTrimPct}
          onCancel={() => setTarget(null)}
          onConfirm={(q, pxx) => record(target, q, pxx)}
        />
      ) : null}

      {releaseTarget ? (
        <EarlyReleaseModal
          lock={releaseTarget}
          busy={busy}
          recordedBy={recordedBy}
          onCancel={() => setReleaseTarget(null)}
          onConfirm={(category, reasoning) => release(releaseTarget, category, reasoning)}
        />
      ) : null}
    </div>
  );
};

export default HarvestDecisionPanel;
